import IntlMessageFormat from 'intl-messageformat';
import * as m from './messages';
import { isValidNumber } from 'libphonenumber-js';

const translate = (message, values = {}) => {
  const messageFormat = new IntlMessageFormat(
    message,
    global.translations.locale
  );
  return messageFormat.format(values);
};

export const required = value => (value ? undefined : translate(m.REQUIRED));

export const len = length => value =>
  value && value.length !== length
    ? translate(m.STRING_LENGTH, { length })
    : undefined;

export const maxLength = length => value =>
  value && value.length > length
    ? translate(m.MAX_LENGTH, { length })
    : undefined;

export const minLength = length => value =>
  value && value.length < length
    ? translate(m.MIN_LENGTH, { length })
    : undefined;

export const number = value =>
  value && isNaN(Number(value)) ? translate(m.NUMBER) : undefined;

export const minValue = min => value =>
  value && value < min ? translate(m.MIN_VALUE, { min_value: min }) : undefined;

export const maxValue = max => value =>
  value && value > max ? translate(m.MAX_VALUE, { max_value: max }) : undefined;

export const email = value =>
  value && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value)
    ? translate(m.INVALID_EMAIL)
    : undefined;

export const alphaNumeric = value =>
  value && /[^a-zA-Z0-9 ]/i.test(value) ? translate(m.ALPHANUMERIC) : undefined;

export const basicPhoneNumber = value =>
  // Regex is from https://stackoverflow.com/a/29767609/556510
  value &&
  !/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im.test(value)
    ? translate(global.translations.messages[m.INVALID_PHONE])
    : undefined;

export const countryPhoneNumber = (value, optionalCountry) =>
  value && !isValidNumber(value, optionalCountry)
    ? translate(global.translations.messages[m.INVALID_PHONE])
    : undefined;

export const oneOf = validValues => value =>
  validValues.indexOf(value) === -1 ? translate(m.NOT_IN_LIST) : undefined;

export const usZipcodeRegex = /(^\d{5}$)|(^\d{5}-\d{4}$)/;
export const zipcode = value =>
  value && !usZipcodeRegex.test(value)
    ? translate(m.INVALID_ZIPCODE)
    : undefined;

export const state = states => oneOf(states.map(s => s.value));

export const ukZipcodeRegex = /([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9]?[A-Za-z])))) {0,1}[0-9][A-Za-z]{2})/;
export const ukZipCode = value =>
  value && !ukZipcodeRegex.test(value) ? 'Invalid postal code' : undefined;

// Israel zip codes are either 5 or 7 digits
export const ilZipCode = value =>
  value && value.length !== 5 && value.length !== 7
    ? translate(m.INVALID_ZIPCODE)
    : undefined;

// Canadian postal codes are 6 characters, alternating letter then number, with an optional space in the middle.
export const caZipCodeRegex = /^[ABCEGHJKLMNPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ ]?\d[ABCEGHJ-NPRSTV-Z]\d$/i;
export const caZipCode = value => {
  return !caZipCodeRegex.test(value) ? translate(m.INVALID_ZIPCODE) : undefined;
};

// Swizz postal codes are 4 digits. First is district, second is area, third is route, fourth is post office number.
// source: https://www.grcdi.nl/gsb/switzerland.html
export const chZipCodeRegex = /^[1-9]\d{3}$/;
export const chZipCode = value =>
  value && !chZipCodeRegex.test(value)
    ? translate(m.INVALID_ZIPCODE)
    : undefined;

// German postal codes of the form: 'DDDDD'. All digits arse used. First digit is the distribution zone,
// second a distribution region. Other digits indicate the distribution district and postal town.
// source: https://www.grcdi.nl/gsb/germany.html
export const deZipCodeRegex = /^((0)[1-4|6-9]\d{3}|(4)[0-2|4-9]\d{3}|(6)[0-1|3-9]\d{3}|[1-3|5|7-9]\d{4})$/;
export const deZipCode = value =>
  value && !deZipCodeRegex.test(value)
    ? translate(m.INVALID_ZIPCODE)
    : undefined;

// Spain postal codes of the form: 'DDDDD'. All digits are used. First two
// digits indicates the province. The third a place within the province.
// source: https://www.grcdi.nl/gsb/spain.html
export const esZipCodeRegex = /^((0)[1-9]\d{3,3}|[1-4]\d{4,4}|(5)[0-2]\d{3,3})$/;
export const esZipCode = value =>
  value && !esZipCodeRegex.test(value)
    ? translate(m.INVALID_ZIPCODE)
    : undefined;

// French postal codes of the form: 'DDDDD'. All digits are used. First two digits indicate the department,
// and range from 01 to 98, or 00 for army. Metropolitan France has postal codes commencing 00-95 and 99.
// source: https://www.grcdi.nl/gsb/france.htm
export const frZipCodeRegex = /^([0-8][0-9]\d{3}|[9][0-6]\d{3}|(99)\d{3})$/;
export const frZipCode = value => {
  return value && !frZipCodeRegex.test(value)
    ? translate(m.INVALID_ZIPCODE)
    : undefined;
};

// Russia Postal codes consist of a block of 6 digits. The first three digits indicate the region or large town,
// the last three the post office. Postal codes can begin with a 1-4 or 6.
// No codes currently begin with 22, 25-29, 31-33, 37, 47-49, 5 or 7-9.
// source: https://www.grcdi.nl/gsb/russia.html
export const ruZipCodeRegex = /^([1|6]\d{5}|[2][0|1|3|4]\d{4}|[3][0|4|5|6|8|9]\d{4}|[4][0-6]\d{4})$/;
export const ruZipCode = value =>
  value && !ruZipCodeRegex.test(value)
    ? translate(m.INVALID_ZIPCODE)
    : undefined;

// Italy postal codes consist of a block of 5 digits, and are highly correlated with administrative districts.
// No codes currently begin with 49, 68, 69, 76, 77, 78, 79, 99
// source: https://www.grcdi.nl/gsb/italy.html
export const itZipCodeRegex = /^(?!49|68|69|76|77|78|79|99)[0-9]{5}$/;
export const itZipCode = value =>
  value && !itZipCodeRegex.test(value)
    ? translate(m.INVALID_ZIPCODE)
    : undefined;

// South Korea postal codes consist of a block of 5 or 6 digits
export const krZipCodeRegex = /^\d{3}(?:\d{2}|-\d{3})$/;
export const krZipCode = value =>
  value && !krZipCodeRegex.test(value)
    ? translate(m.INVALID_ZIPCODE)
    : undefined;

// Poland postal codes have 5 digits.
// source: https://www.grcdi.nl/gsb/poland.html
export const plZipCodeRegex = /^(\d{2,2}(-)\d{3,3})$/;
export const plZipCode = value =>
  value && !plZipCodeRegex.test(value)
    ? translate(m.INVALID_ZIPCODE)
    : undefined;

// Ireland postal codes consist of 7 digits of letters and numbers, not including B, G, I, J, L, M, O, Q or S
// regex source: https://github.com/dgilperez/validates_zipcode/blob/f0415c1b3864a9888b3310e39c98f625aa5f1a0c/lib/validates_zipcode/cldr_regex_collection.rb#L16
export const ieZipCodeRegex = /^([AC-FHKNPRTV-Y]\d{2}|D6W)\s?[0-9AC-FHKNPRTV-Y]{4}$/;
export const ieZipCode = value =>
  value && !ieZipCodeRegex.test(value)
    ? translate(m.INVALID_ZIPCODE)
    : undefined;

// If the country requires a zipcode, or if one is passed in, validate the zipcode with appropriate country
// Otherwise, pass undefined to mark it validated
const zipWrapper = country_validator => {
  return value => {
    if (gon.zipcode_required || !!value) {
      return country_validator(value);
    }
    return undefined;
  };
};

export const getZipValidator = (country = 'US') => {
  const countryMap = {
    US: zipcode,
    GB: ukZipCode,
    IL: ilZipCode,
    CA: caZipCode,
    FR: frZipCode,
    CH: chZipCode,
    DE: deZipCode,
    ES: esZipCode,
    RU: ruZipCode,
    IT: itZipCode,
    KR: krZipCode,
    PL: plZipCode,
    IE: ieZipCode,
  };
  return zipWrapper(countryMap[country]);
};

export const deAddress1Regex = /^(?![0-9]).*$/;
export const deAddress1 = value =>
  value && !deAddress1Regex.test(value)
    ? translate(m.INVALID_DE_ADDRESS1)
    : undefined;

export const getAddress1Validator = country => {
  const countryMap = { DE: deAddress1 };
  return countryMap[country] || (() => undefined);
};

// Cybersource gateway has a max length of 35 for any address field
const maxLength35 = maxLength(35);
export const cybersource = value => maxLength35(value);

const minLength6 = minLength(6);
export const password = value => minLength6(value);

export const passwordsMatch = values =>
  values.password !== values.password_confirmation
    ? translate(m.PASSWORDS_MATCH)
    : undefined;

export const notOnlyWhitespace = field => {
  const regex = /\S/;

  return !regex.test(field)
    ? translate(global.translations.messages[m.NOT_ONLY_WHITESPACE])
    : undefined;
};

// PASSPORT NUMBER

// Russian domestic passport numbers are 10 digits
// https://sayari.com/decoding-russian-passport-numbers/
export const ruPassportNumberRegex = /^([0-9]{10})$/;
export const ruPassportNumber = value =>
  value && !ruPassportNumberRegex.test(value)
    ? translate(m.INVALID_PASSPORT_NUMBER)
    : undefined;

export const getPassportNumberValidator = country => {
  const countryMap = {
    RU: ruPassportNumber,
  };

  return countryMap[country] || (() => undefined);
};
